import {
  useInfiniteQuery,
  useQuery,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { conversationDetailResponseSchema } from '@shared/api';
import type { ConversationDetail, Message } from '@shared/types';
import { api, qs } from '@web/lib/api';
import { keys } from '@web/lib/keys';
import type { MessagePage } from '@web/lib/realtime';
import { socket, unwatchThread, watchThread } from '@web/lib/socket';

const PAGE_SIZE = 50;

/**
 * The conversation itself.
 *
 * Fetched separately from its messages, with `limit=1`, so the two live in
 * different cache entries: the realtime bridge patches the header (assignee,
 * state, facts) on every `conversation.updated` without touching the message
 * list, and paging back through history never rewrites the header. The cost is
 * one extra small request when a thread opens, which is the right trade for a
 * cache whose two halves cannot corrupt each other.
 */
export function useConversation(conversationId: string): UseQueryResult<ConversationDetail> {
  return useQuery({
    queryKey: keys.conversation(conversationId),
    queryFn: async () => {
      const response = await api.get(
        `/api/conversations/${conversationId}${qs({ limit: 1 })}`,
        conversationDetailResponseSchema,
      );
      return response.conversation;
    },
  });
}

/**
 * The transcript, newest page first.
 *
 * "Next" means OLDER here — the server pages backwards through history and the
 * fetch is triggered by scrolling UP. `maxPages` caps the memory a tab that has
 * been open since breakfast can accumulate; v5 drops the far end for us.
 */
export function useMessages(
  conversationId: string,
): UseInfiniteQueryResult<{ pages: MessagePage[] }> {
  return useInfiniteQuery({
    queryKey: keys.messages(conversationId),
    queryFn: async ({ pageParam }) => {
      const response = await api.get(
        `/api/conversations/${conversationId}${qs({ limit: PAGE_SIZE, cursor: pageParam ?? undefined })}`,
        conversationDetailResponseSchema,
      );
      return response.messages;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last: MessagePage) => last.nextCursor,
    maxPages: 6,
  });
}

/**
 * Flatten to render order: oldest at the top.
 *
 * `prepended` is how many messages sit above the first page, and it is what
 * Virtuoso's `firstItemIndex` counts down from. Both are derived from the same
 * query data in the same render, which is the entire trick to prepending
 * without the list jumping: two separate state updates would land in two
 * renders and the scroll position would snap.
 */
export function useFlatMessages(pages: MessagePage[] | undefined): {
  messages: Message[];
  prepended: number;
} {
  return useMemo(() => {
    const list = pages ?? [];
    const messages = [...list].reverse().flatMap((page) => [...page.items].reverse());
    const prepended = list.slice(1).reduce((total, page) => total + page.items.length, 0);
    return { messages, prepended };
  }, [pages]);
}

/**
 * Join the room that carries this thread's messages, and leave it on the way
 * out. Without the unwatch, an operator who has visited forty conversations in
 * a shift is receiving forty threads' worth of traffic.
 */
export function useWatchThread(conversationId: string): void {
  useEffect(() => {
    if (socket.connected) watchThread(conversationId);
    const onConnect = (): void => {
      watchThread(conversationId);
    };
    socket.on('connect', onConnect);

    return () => {
      socket.off('connect', onConnect);
      if (socket.connected) unwatchThread(conversationId);
    };
  }, [conversationId]);
}
